import { FC, useState } from 'react';
import {
  Controller,
  DefaultValues,
  FormProvider,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { chakra, HStack, SlideFade, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import Heading from 'components/_core/Heading';
import SunshineIcon from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';
import ReceiptUpload from 'components/ReceiptUpload';
import StepperProgressBar from 'components/Stepper/StepperProgressBar';
import * as Layout from 'layouts/StickyStepperLayout';

import { VatItem } from './components/VatItem';
import locales from './locales';
import {
  ReceiptInformationFormSchema,
  receiptUploadFormSchema,
} from './schema';

interface ReceiptInformationFormProps {
  onSubmit: SubmitHandler<ReceiptInformationFormSchema>;
  defaultValues?: DefaultValues<ReceiptInformationFormSchema>;
}

export const ReceiptInformationForm: FC<ReceiptInformationFormProps> = ({
  defaultValues,
  onSubmit,
}) => {
  const [uploadsCount, setUploadsCount] = useState(0);
  const form = useForm<ReceiptInformationFormSchema>({
    defaultValues,
    mode: 'onChange',
    resolver: zodResolver(receiptUploadFormSchema),
  });
  const { fields, insert, remove } = useFieldArray({
    control: form.control,
    name: 'amount.vatItems',
  });

  const { isSubmitting, isValid } = form.formState;

  const shouldShowInputs =
    !!defaultValues?.supportingDocument?.fileRef || uploadsCount > 0;

  return (
    <FormProvider {...form}>
      <chakra.form display="contents" onSubmit={form.handleSubmit(onSubmit)}>
        <Layout.ContentsContainer gap="space-40">
          <VStack align="flex-start" spacing="space-16">
            <Heading size="lg">{locales.title}</Heading>
          </VStack>
          <VStack alignItems="stretch" spacing="space-24">
            <Form.Field>
              <Controller
                control={form.control}
                name="supportingDocument"
                render={({ field }) => (
                  <ReceiptUpload
                    onReceiptRemoved={() => {
                      field.onChange(null);
                    }}
                    onReceiptUploaded={(receipt) => {
                      field.onChange(receipt);
                      setUploadsCount((v) => v + 1);
                    }}
                    receipt={field.value}
                  />
                )}
              />
            </Form.Field>
            {!shouldShowInputs ? (
              <HStack alignItems="flex-start">
                <SunshineIcon name="information" size="icon-16" />
                <Text variant="secondary">
                  {locales.supportingDocumentTypes.label}
                </Text>
              </HStack>
            ) : null}
            <SlideFade
              in={shouldShowInputs}
              initial={false}
              offsetY={100}
              transition={{ enter: { duration: 0.3 }, exit: { duration: 0 } }}
            >
              <VStack alignItems="stretch" spacing="space-24">
                <Form.Field
                  error={form.formState.errors.amount?.message}
                  label={locales.labels.totalAmount}
                >
                  <Form.AmountInput
                    placeholder={locales.placeholders.totalAmount}
                    {...form.register('amount.total', {
                      valueAsNumber: true,
                    })}
                  />
                </Form.Field>
                <VStack alignItems="stretch">
                  {fields.map((field, index) => (
                    <VatItem
                      index={index}
                      isTrashButtonDisabled={fields.length === 1}
                      key={field.id}
                      onAddItem={(i) => {
                        // insert is forcing us to pass defined values, unlike defaultValues where we can pass nullish values.
                        // Because we don't want to update the zod schema to allow nullish values, we are passing an empty object
                        // and react hook form will happily populate those values when needed.
                        insert(i + 1, {} as never, {
                          focusName: `amount.vatItems.${i + 1}.rate`,
                        });
                      }}
                      onRemoveItem={remove}
                      showAddButton={fields.length - 1 === index}
                    />
                  ))}
                </VStack>
                <Form.Field
                  error={form.formState.errors.description?.message}
                  label={locales.labels.description}
                >
                  <Form.Input
                    placeholder={locales.placeholders.description}
                    {...form.register('description')}
                  />
                </Form.Field>
                <Form.Field
                  error={form.formState.errors.reseller?.message}
                  label={locales.labels.reseller}
                >
                  <Form.Input
                    placeholder={locales.placeholders.reseller}
                    {...form.register('reseller')}
                  />
                </Form.Field>
              </VStack>
            </SlideFade>
          </VStack>
        </Layout.ContentsContainer>
        <Layout.FooterContainer>
          <StepperProgressBar />

          <Layout.FooterButtonsContainer>
            <Button
              isDisabled={!isValid || isSubmitting}
              isLoading={isSubmitting}
              type="submit"
            >
              {locales.submit}
            </Button>
          </Layout.FooterButtonsContainer>
        </Layout.FooterContainer>
      </chakra.form>
    </FormProvider>
  );
};
